import React, { useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import styled, { css, keyframes } from 'styled-components';
import { PiBrainFill } from 'react-icons/pi';
import { Button, Flex, Icon, Text } from 'core/components';
import { ShimmerText } from '../KaizenChatSession.styles';
import StreamingMarkdown from './StreamingMarkdown';

// within this distance of the bottom, the panel keeps following the newest thinking
const STICK_TO_BOTTOM_PX = 24;

const flow = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`;

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Sits on top of the chat input, inset a little so it reads as attached to it
const Panel = styled.div`
  position: relative;
  margin: 0 14px -1px;
  border: ${({ theme }) => theme.borders.thin};
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  background: ${({ theme }) => theme.backgrounds.alternate};
  overflow: hidden;
  animation: ${rise} 240ms cubic-bezier(0.33, 1, 0.68, 1) both;

  // a band of color along the top that flows while the model is thinking
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 2px;
    background: linear-gradient(90deg, #ed698a, #76aace, #ff8c2d, #ed698a);
    background-size: 200% 100%;
    opacity: 0.45;
    ${({ $isThinking }) =>
      $isThinking &&
      css`
        opacity: 1;
        animation: ${flow} 2.4s linear infinite;
      `}
  }
`;

const Body = styled.div`
  max-height: 140px;
  overflow-y: auto;
  padding: 8px 12px 10px 34px;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.body};
  // older thinking fades out as it scrolls up
  mask-image: linear-gradient(to bottom, transparent 0, #000 18px);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 18px);

  p:last-child {
    margin-bottom: 0;
  }
`;

/**
 * The model's thinking behind the latest turn, as it streams in. Shown above the chat input, collapsed until the user
 * expands it; it is never persisted, so it is gone after a reload.
 */
const ThinkingStream = ({ chatSessionModel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const bodyRef = useRef(null);
  const contentRef = useRef(null);
  const stickToBottom = useRef(true);

  const text = chatSessionModel?.latestTurnThinking;
  const turnId = chatSessionModel?.get('messages')?.at(-1)?.agentTurn?.at(-1)?.id;
  const isThinking = Boolean(chatSessionModel?.isStreamingThinking);
  const hasText = Boolean(text);

  // a new turn's thinking is followed again, even when the user scrolled up to read the last one
  useLayoutEffect(() => {
    stickToBottom.current = true;
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [turnId]);

  // follow the text as it is revealed, unless the user has scrolled up to read
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const content = contentRef.current;
    if (!body || !content || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (stickToBottom.current) {
        body.scrollTop = body.scrollHeight;
      }
    });
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [isOpen, hasText]);

  if (!hasText) {
    return null;
  }

  const handleScroll = () => {
    const body = bodyRef.current;
    stickToBottom.current = body.scrollHeight - body.scrollTop - body.clientHeight < STICK_TO_BOTTOM_PX;
  };

  return (
    <Panel $isThinking={isThinking} data-testid="kaizen-thinking-stream">
      <Flex alignItems="center" gap="6px" pl="12px" pr="4px" pt="4px">
        <Icon icon={PiBrainFill} color="muted" iconSize={14} />
        <Text small muted fontWeight="bold" style={{ flex: 1 }}>
          {isThinking ? <ShimmerText>Reasoning</ShimmerText> : 'Reasoning'}
        </Text>
        <Button
          small
          minimal
          icon={isOpen ? 'chevron-down' : 'chevron-up'}
          ariaLabel={isOpen ? 'Collapse reasoning' : 'Expand reasoning'}
          onClick={() => setIsOpen(!isOpen)}
        />
      </Flex>
      {isOpen && (
        <Body ref={bodyRef} onScroll={handleScroll}>
          <div ref={contentRef}>
            <StreamingMarkdown text={text} isStreaming={isThinking} streamKey={`${turnId}:thinking`} />
          </div>
        </Body>
      )}
    </Panel>
  );
};

export default observer(ThinkingStream);
